import React, { useState, useEffect, useCallback } from 'react';
import { authApi, barrierApi, restaurantApi } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const WEEKDAYS = ['B.e', 'C.a', 'Çar', 'C.ax', 'Cüm', 'Şnb', 'Baz'];
const METHOD_COLORS = { internet: '#10b981', bluetooth: '#3b82f6', call: '#f59e0b', app: '#8b5cf6', wifi: '#06b6d4', admin: '#f59e0b', unknown: '#475569' };

function buildWeekData(logs) {
  const counts = {};
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const key = d.toDateString();
    counts[key] = { gun: WEEKDAYS[d.getDay()], açılma: 0 };
  }
  logs.forEach(l => {
    const key = new Date(l.createdAt || l.timestamp).toDateString();
    if (counts[key] && l.success !== false) counts[key].açılma++;
  });
  return Object.values(counts);
}

function buildMethodData(logs) {
  const map = {};
  logs.forEach(l => {
    const m = (l.method || 'unknown').toLowerCase();
    map[m] = (map[m] || 0) + 1;
  });
  return Object.entries(map).map(([name, value]) => ({ name, value, color: METHOD_COLORS[name] || '#475569' }));
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload?.length) {
    return (
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text)' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
        {payload.map(p => <div key={p.dataKey} style={{ color: p.color }}>{p.name}: <b>{p.value}</b></div>)}
      </div>
    );
  }
  return null;
}

export default function Analytics() {
  const [userStats, setUserStats] = useState(null);
  const [barriers, setBarriers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [systemStatus, setSystemStatus] = useState({ api: null, mqtt: null });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [usersRes, barriersRes, restaurantsRes] = await Promise.allSettled([
      authApi.stats(),
      barrierApi.list(),
      restaurantApi.list(),
    ]);
    if (usersRes.status === 'fulfilled') setUserStats(usersRes.value);
    const bList = barriersRes.status === 'fulfilled'
      ? (Array.isArray(barriersRes.value) ? barriersRes.value : (barriersRes.value?.data || []))
      : [];
    setBarriers(bList);
    if (restaurantsRes.status === 'fulfilled') {
      const rList = Array.isArray(restaurantsRes.value) ? restaurantsRes.value : (restaurantsRes.value?.data || []);
      setRestaurants(rList);
    }

    // Fetch logs from up to 8 barriers
    const logs = [];
    await Promise.allSettled(bList.slice(0, 8).map(b =>
      barrierApi.logs(b.id).then(d => {
        const rows = Array.isArray(d) ? d : (d?.data || d?.logs || []);
        logs.push(...rows);
      })
    ));
    setAllLogs(logs);

    // System status
    setSystemStatus(prev => ({ ...prev, api: true }));
    barrierApi.debug().then(d => {
      setSystemStatus({ api: true, mqtt: d?.mqttConnected ?? d?.mqtt ?? null });
    }).catch(() => setSystemStatus({ api: true, mqtt: null }));

    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onlineBarriers = barriers.filter(b => b.isOnline).length;
  const offlineBarriers = barriers.length - onlineBarriers;
  const todayStr = new Date().toDateString();
  const todayLogs = allLogs.filter(l => new Date(l.createdAt || l.timestamp).toDateString() === todayStr);
  const todayOpens = todayLogs.filter(l => l.success !== false).length;
  const weekData = buildWeekData(allLogs);
  const methodData = buildMethodData(allLogs);
  const barrierStatusData = [
    { name: 'Online', value: onlineBarriers, color: '#10b981' },
    { name: 'Offline', value: offlineBarriers, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const cards = [
    { label: 'Ümumi İstifadəçi', value: userStats?.total ?? '—', color: '#3b82f6', icon: '◎', sub: userStats ? `${userStats.newThisWeek || 0} bu həftə` : '' },
    { label: 'Şlaqbaumlar', value: barriers.length, color: '#8b5cf6', icon: '⊞', sub: `${onlineBarriers} online` },
    { label: 'Bu gün açılma', value: todayOpens, color: '#10b981', icon: '↑', sub: `${allLogs.length} ümumi log` },
    { label: 'Restoranlar', value: restaurants.length, color: '#f59e0b', icon: '◉', sub: 'Aktiv' },
  ];

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Analitika</h1>
          <p style={S.sub}>Son yenilənmə: {lastRefresh.toLocaleTimeString('az-AZ', { timeZone:'Asia/Baku', hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false })}</p>
        </div>
        <button style={S.refreshBtn} onClick={fetchAll} disabled={loading}>
          {loading ? '⟳ Yüklənir...' : '⟳ Yenilə'}
        </button>
      </div>

      {/* Stat kartlar */}
      <div style={S.grid4}>
        {cards.map(c => (
          <div key={c.label} style={S.statCard}>
            <div style={{ ...S.icon, color: c.color, background: c.color + '22' }}>{c.icon}</div>
            <div style={S.statVal}>{loading ? <span style={S.skel}>—</span> : c.value}</div>
            <div style={S.statLabel}>{c.label}</div>
            {c.sub && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Həftəlik açılma trendi */}
        <div style={S.chartCard}>
          <h3 style={S.chartTitle}>Şlaqbaum Açılması — Son 7 Gün</h3>
          {allLogs.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0', fontSize: 13 }}>Log məlumatı yoxdur</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={weekData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <XAxis dataKey="gun" stroke="var(--border)" tick={{ fill: 'var(--text2)', fontSize: 12 }} />
                <YAxis stroke="var(--border)" tick={{ fill: 'var(--text2)', fontSize: 12 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="açılma" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Açılma" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Şlaqbaum statusu pie */}
        <div style={S.chartCard}>
          <h3 style={S.chartTitle}>Şlaqbaum Statusu</h3>
          {barriers.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0', fontSize: 13 }}>Şlaqbaum yoxdur</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={barrierStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                  dataKey="value" nameKey="name" paddingAngle={3}>
                  {barrierStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Açılma metodu paylanması */}
      {methodData.length > 0 && (
        <div style={S.chartCard}>
          <h3 style={S.chartTitle}>Açılma Metodu Paylanması</h3>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            <ResponsiveContainer width={300} height={180}>
              <PieChart>
                <Pie data={methodData} cx="50%" cy="50%" outerRadius={70}
                  dataKey="value" nameKey="name" paddingAngle={2}>
                  {methodData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {methodData.map(m => {
                const pct = allLogs.length ? Math.round((m.value / allLogs.length) * 100) : 0;
                return (
                  <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--text2)', width: 90, textTransform: 'capitalize' }}>{m.name}</span>
                    <div style={{ flex: 1, height: 6, background: 'var(--bg2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: m.color, borderRadius: 4, transition: 'width 0.5s' }} />
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text3)', width: 55, textAlign: 'right' }}>{m.value} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Sistem statusu */}
      <div style={S.chartCard}>
        <h3 style={S.chartTitle}>Sistem Statusu</h3>
        <div style={S.statusGrid}>
          {[
            { name: 'Backend API', port: 3000, ok: systemStatus.api },
            { name: 'MQTT Broker', port: 1883, ok: systemStatus.mqtt },
            { name: 'PostgreSQL', port: 5434, ok: null },
            { name: 'Redis Cache', port: 6380, ok: null },
            { name: 'Admin Panel', port: 3000, ok: null },
            { name: 'Web QR Menyu', port: 3001, ok: null },
          ].map(s => (
            <div key={s.name} style={S.statusItem}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: s.ok === null ? '#f59e0b' : s.ok ? '#10b981' : '#ef4444',
                boxShadow: s.ok ? '0 0 6px #10b98166' : 'none',
              }} />
              <div style={{ flex: 1 }}>
                <div style={S.statusName}>{s.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>:{s.port}</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                color: s.ok === null ? '#f59e0b' : s.ok ? '#10b981' : '#ef4444',
                background: (s.ok === null ? '#f59e0b' : s.ok ? '#10b981' : '#ef4444') + '22',
              }}>
                {s.ok === null ? 'Yoxlanılır' : s.ok ? 'Aktiv' : 'Xəta'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Yolharitası */}
      <div style={S.chartCard}>
        <h3 style={S.chartTitle}>Layihə Yol Xəritəsi</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { phase: 'Faza 1', name: 'Şlaqbaum Sistemi', done: true },
            { phase: 'Faza 2', name: 'Restoran QR Sifarişi', done: true },
            { phase: 'Faza 3', name: 'Wallet & Ödəmələr', done: false },
            { phase: 'Faza 4', name: 'Taxi İnteqrasiyası', done: false },
            { phase: 'Faza 5', name: 'Bərbər / Salon', done: false },
            { phase: 'Faza 6', name: 'Super-App', done: false },
          ].map((r) => (
            <div key={r.phase} style={{ flex: '1 1 140px', background: 'var(--bg2)', borderRadius: 10, padding: 14,
              border: `1px solid ${r.done ? 'var(--success)40' : 'var(--border)'}` }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{r.phase}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: r.done ? 'var(--success)' : 'var(--text3)', fontSize: 14 }}>{r.done ? '✓' : '○'}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: r.done ? 'var(--success)' : 'var(--text)' }}>{r.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--text)', marginBottom: 4 },
  sub: { color: 'var(--text3)', fontSize: 13 },
  refreshBtn: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 20 },
  statCard: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
  icon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, marginBottom: 12 },
  statVal: { fontSize: 32, fontWeight: 800, color: 'var(--text)', marginBottom: 4 },
  statLabel: { fontSize: 13, color: 'var(--text2)' },
  skel: { color: 'var(--border)', background: 'var(--border)', borderRadius: 4 },
  chartCard: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 16 },
  chartTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 20 },
  statusGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 },
  statusItem: { display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', borderRadius: 10, padding: 14 },
  statusName: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
};
